import React from 'react';
import { Calendar, Pencil, X } from 'lucide-react';
import { MONTHS } from '../constants/family';
import Button from './ui/Button';
import Input from './ui/Input';
import Select from './ui/Select';

export default function HistoricalDatePicker({ label, value, onChange, theme, minYear = 1880, maxYear = new Date().getFullYear() }) {
  // Determine if the current value is a structured date (YYYY, YYYY-MM, YYYY-MM-DD) or fuzzy text
  const isStructured = (v) => {
    if (!v) return true;
    return /^\d{4}(-\d{1,2}(-\d{1,2})?)?$/.test(String(v).trim());
  };

  const parse = (v) => {
    if (!v) return { year: '', month: '', day: '' };
    const s = String(v).trim();
    const parts = s.split('-');
    if (parts.length >= 1 && /^\d{4}$/.test(parts[0])) {
      return {
        year:  parts[0] || '',
        month: parts[1] ? String(parseInt(parts[1])) : '',
        day:   parts[2] ? String(parseInt(parts[2])) : '',
      };
    }
    return { year: '', month: '', day: '' };
  };

  const [parts, setParts] = React.useState(() => parse(value));
  const [textMode, setTextMode] = React.useState(() => !isStructured(value) && !!value);

  React.useEffect(() => {
    if (textMode) return;
    setParts(parse(value));
  }, [value, textMode]);

  const emit = (next) => {
    const { year, month, day } = next;
    if (!year) { onChange(''); return; }
    let out = year.padStart(4, '0');
    if (month) out += '-' + month.padStart(2, '0');
    if (month && day) out += '-' + day.padStart(2, '0');
    onChange(out);
  };

  const set = (key, val) => {
    const next = { ...parts, [key]: val };
    if (key === 'year'  && !val) { next.month = ''; next.day = ''; }
    if (key === 'month' && !val) { next.day = ''; }
    // Changing the month or year can leave a day that does not exist in the new
    // month (31 Jan → Feb, 29 Feb → a non-leap year). Without this the picker
    // emits an impossible date like 1943-02-31 while the day <select> silently
    // falls back to its blank placeholder — value and UI disagree.
    if ((key === 'month' || key === 'year') && val && next.day && next.year && next.month) {
      const dim = new Date(parseInt(next.year), parseInt(next.month), 0).getDate();
      if (parseInt(next.day) > dim) next.day = '';
    }
    setParts(next);
    emit(next);
  };

  const daysInMonth = parts.year && parts.month
    ? new Date(parseInt(parts.year), parseInt(parts.month), 0).getDate()
    : 31;

  const yearOpts = Array.from({ length: maxYear - minYear + 1 }, (_, i) => {
    const y = String(maxYear - i);
    return { value: y, label: y };
  });
  const monthOpts = MONTHS.map((m, i) => ({ value: String(i + 1), label: m }));
  const dayOpts   = Array.from({ length: daysInMonth }, (_, i) => {
    const d = String(i + 1);
    return { value: d, label: d };
  });

  const summary = parts.year
    ? [parts.day && parts.month && `${parseInt(parts.day)}`, parts.month && MONTHS[parseInt(parts.month)-1], parts.year].filter(Boolean).join(' ')
    : '';

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '3px' }}>
        <label style={{ fontSize: '0.62rem', fontWeight: 700, color: theme.textSub, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
          {label}
        </label>
        {summary && !textMode && (
          <span style={{ fontSize: '0.68rem', color: theme.text, fontWeight: 400 }}>{summary}</span>
        )}
        <Button
          theme={theme}
          variant="ghost"
          size="sm"
          onClick={() => setTextMode(m => !m)}
          icon={textMode ? Calendar : Pencil}
          title={textMode ? 'Switch to date picker' : 'Switch to text entry for fuzzy dates (e.g. c.1920)'}
          style={{ marginLeft: 'auto' }}
        >
          {textMode ? 'Picker' : 'Text'}
        </Button>
      </div>

      {textMode ? (
        /* Free text mode — for fuzzy dates like c.1920, 1943-1944, "before 1940" */
        <Input
          theme={theme}
          value={value || ''}
          onChange={e => onChange(e.target.value)}
          aria-label={label}
          placeholder="e.g. c.1920, Jun 1926, 1943-1944"
        />
      ) : (
        /* Structured picker mode */
        <div style={{ display: 'flex', gap: '4px' }}>
          <Select
            theme={theme}
            size="sm"
            fullWidth={false}
            value={parts.year}
            onChange={e => set('year', e.target.value)}
            aria-label={`${label} year`}
            style={{ flex: 2, color: parts.year ? theme.text : theme.textSub }}
          >
            <option value="">Year</option>
            {yearOpts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </Select>
          {parts.year && (
            <Select
              theme={theme}
              size="sm"
              fullWidth={false}
              value={parts.month}
              onChange={e => set('month', e.target.value)}
              aria-label={`${label} month`}
              style={{ flex: 2, color: parts.month ? theme.text : theme.textSub }}
            >
              <option value="">Month</option>
              {monthOpts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </Select>
          )}
          {parts.month && (
            <Select
              theme={theme}
              size="sm"
              fullWidth={false}
              value={parts.day}
              onChange={e => set('day', e.target.value)}
              aria-label={`${label} day`}
              style={{ flex: 1, color: parts.day ? theme.text : theme.textSub }}
            >
              <option value="">Day</option>
              {dayOpts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </Select>
          )}
          {parts.year && (
            <Button
              theme={theme}
              variant="outline"
              size="sm"
              onClick={() => { setParts({ year:'', month:'', day:'' }); onChange(''); }}
              icon={X}
              ariaLabel="Clear"
              title="Clear"
              style={{ flexShrink: 0 }}
            />
          )}
        </div>
      )}
    </div>
  );
}
